import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import {
  getSettingsSchema,
  updateSettingsSchema,
} from "@services/adminService";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

const Container = styled.div`
  max-width: 900px;
  width: 100%;
`;

const VersionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const VersionInput = styled.input`
  height: 36px;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  width: 200px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    max-width: 200px;
  }
  &:focus {
    border-color: #68ad18;
  }
  &:disabled {
    background: #f8f8f8;
    color: #899093;
  }
`;

const SchemaTextarea = styled.textarea`
  width: 100%;
  min-height: 400px;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  font-family: monospace;
  font-size: 13px;
  padding: 16px;
  color: #494c5b;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
  line-height: 1.5;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-height: 250px;
    font-size: 12px;
    padding: 12px;
  }
  &:focus {
    border-color: #68ad18;
  }
  &:disabled {
    background: #f8f8f8;
    color: #899093;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const ActionButton = styled(Button)`
  width: auto;
  min-width: 100px;
`;

const Message = styled.div<{ error?: boolean }>`
  margin-top: 12px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: ${({ error }) => (error ? "#e74c3c" : "#68ad18")};
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 0;
`;

const AdminSettingsSchemaTab: React.FC = () => {
  const [version, setVersion] = useState("");
  const [schemaText, setSchemaText] = useState("");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    error: boolean;
  } | null>(null);
  const [hasSchema, setHasSchema] = useState(false);

  const fetchSchema = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSettingsSchema();
      if (data.schema) {
        setVersion(data.version || "");
        setSchemaText(JSON.stringify(data.schema, null, 2));
        setHasSchema(true);
      } else {
        setVersion("1.0.0");
        setSchemaText("{}");
        setHasSchema(false);
      }
    } catch (e) {
      console.error("Failed to fetch schema:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchema();
  }, [fetchSchema]);

  const handleSave = async () => {
    setMessage(null);
    // Validate JSON
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(schemaText);
    } catch (e) {
      setMessage({ text: "Invalid JSON format", error: true });
      return;
    }

    setSaving(true);
    try {
      await updateSettingsSchema({ version, schema: parsed });
      setMessage({ text: "Schema saved successfully", error: false });
      setEditing(false);
      setHasSchema(true);
    } catch (e: any) {
      setMessage({
        text: e.message || "Failed to save schema",
        error: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setMessage(null);
    fetchSchema();
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  return (
    <Container>
      <VersionRow>
        <Text size={14} color="#899093">
          Schema version:
        </Text>
        <VersionInput
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          disabled={!editing}
          placeholder="e.g. 1.0.0"
        />
      </VersionRow>

      {!hasSchema && !editing ? (
        <EmptyState>
          <Text size={14} color="#899093" style={{ marginBottom: 16 }}>
            No settings schema defined yet.
          </Text>
          <ActionButton onClick={() => setEditing(true)}>
            Create Schema
          </ActionButton>
        </EmptyState>
      ) : (
        <>
          <SchemaTextarea
            value={schemaText}
            onChange={(e) => setSchemaText(e.target.value)}
            disabled={!editing}
          />

          <Actions>
            {editing ? (
              <>
                <ActionButton onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </ActionButton>
                <ActionButton kind="outline" onClick={handleCancel}>
                  Cancel
                </ActionButton>
              </>
            ) : (
              <ActionButton kind="outline" onClick={() => setEditing(true)}>
                Edit
              </ActionButton>
            )}
          </Actions>
        </>
      )}

      {message && <Message error={message.error}>{message.text}</Message>}
    </Container>
  );
};

export default AdminSettingsSchemaTab;
